import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch, useSelector } from 'react-redux'
import usePage from '../../hooks/usePage'
import { FlexColumn, FlexRow } from '../../components/atoms/layout/Flex'
import FormItem from '../../components/molecules/inputs/FormItem'
import TextField from '../../components/atoms/inputs/TextField'
import NumberControl from '../../components/atoms/inputs/NumberControl'
import Label from '../../components/atoms/display/Label'
import Button from '../../components/atoms/inputs/Button'
import useValidationRule from '../../hooks/useValidationRule'
import Form from '../../components/atoms/inputs/Form'
import { JobActions } from '../../store/job'
import usePath from '../../hooks/usePath'
import { JobSelectors } from '../../store/job/selectors'
import useRouter from '../../hooks/useRouter'

type JobFormParams = {
  establishedYear?: number | null
  numberOfEmployees: number
  siteUrl?: string | null
  lineId: string
}

export default function JobEditPage() {
  const { changeTitle } = usePage()
  const rules = useValidationRule()
  const dispatch = useDispatch()
  const { job: jobPage } = usePath()
  const { job: toJob } = useRouter()
  const job = useSelector(JobSelectors.job)
  const [initialize, setInitialize] = useState(false)

  const form = useForm<JobFormParams>({
    resolver: yupResolver(rules.job),
    mode: 'onChange',
  })
  const { errors } = form.formState

  useEffect(() => {
    changeTitle('求人管理', '企業詳細編集')
  }, [changeTitle])

  useEffect(() => {
    dispatch(JobActions.fetchJob())
  }, [dispatch])

  useEffect(() => {
    if (job) {
      form.setValue('establishedYear', job.establishedYear)
      form.setValue('numberOfEmployees', job.numberOfEmployees)
      form.setValue('siteUrl', job.siteUrl)
    }
    setInitialize(true)
  }, [form, job])

  const handleSave = useCallback(
    (params: JobFormParams) => {
      if (job) {
        dispatch(
          JobActions.updateJob({
            establishedYear: params.establishedYear ?? null,
            numberOfEmployees: params.numberOfEmployees,
            siteUrl: params.siteUrl ?? null,
            lineId: params.lineId,
            getRedirectPath: jobPage,
          })
        )
      } else {
        dispatch(
          JobActions.createJob({
            establishedYear: params.establishedYear ?? null,
            numberOfEmployees: params.numberOfEmployees,
            siteUrl: params.siteUrl ?? null,
            lineId: params.lineId,
            getRedirectPath: jobPage,
          })
        )
      }
    },
    [dispatch, jobPage, job]
  )

  if (!initialize) {
    return null
  }

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(handleSave)}>
        <FlexColumn>
          <FormItem label="設立年">
            <FlexRow align="center">
              <NumberControl
                control={form.control}
                name="establishedYear"
                size="sm"
                error={errors.establishedYear?.message}
              />
              <Label text="年" />
            </FlexRow>
          </FormItem>
          <FormItem label="従業員数">
            <FlexRow align="center">
              <NumberControl
                control={form.control}
                name="numberOfEmployees"
                size="sm"
                error={errors.numberOfEmployees?.message}
              />
              <Label text="人" />
            </FlexRow>
          </FormItem>
          <FormItem label="ホームページURL">
            <TextField size="lg" register={form.register('siteUrl')} error={errors.siteUrl?.message} />
          </FormItem>
          <FlexRow align="center">
            <Button onClick={toJob} color="secondary">
              キャンセル
            </Button>
            <Button type="submit">保存</Button>
          </FlexRow>
        </FlexColumn>
      </Form>
    </FormProvider>
  )
}
